import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, buildWhatsAppLink } from "@/data/site";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const card = {
  hidden: { y: 40, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const ServiceCard = ({ service }) => {
  const Icon = service.icon;
  return (
    <motion.div
      variants={card}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
      data-testid={`service-card-${service.id}`}
    >
      <div className="relative h-52 overflow-hidden sm:h-56">
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
        <div className="absolute left-4 top-4 grid h-12 w-12 place-items-center rounded-2xl bg-white/90 text-[var(--kilic-blue)] shadow-lg backdrop-blur">
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="absolute inset-x-0 bottom-0 p-5 font-display text-xl font-extrabold text-white sm:text-2xl">
          {service.title}
        </h3>
      </div>
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <p className="flex-1 text-sm leading-relaxed text-neutral-600">
          {service.text}
        </p>
        <a
          href={buildWhatsAppLink({
            service: service.title,
            message: `Ich interessiere mich für ${service.title} und hätte gern ein Angebot.`,
          })}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`service-cta-${service.id}`}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--kilic-green-dark)] transition-colors hover:text-[var(--kilic-blue)]"
        >
          Angebot anfragen
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section id="leistungen" className="relative bg-white py-20 md:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[var(--kilic-green-dark)]">
            Unsere Dienstleistungen
          </span>
          <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tighter text-neutral-900 sm:text-4xl md:text-5xl">
            Alles für ein makelloses{" "}
            <span className="text-gradient">Zuhause & Gebäude</span>
          </h2>
          <p className="mt-5 text-sm text-neutral-600 sm:text-base md:text-lg">
            Von kristallklaren Fenstern bis zur gepflegten Fassade – wir bringen
            Ihre Immobilie zum Strahlen.
          </p>
        </motion.div>

        <motion.div
          variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
        >
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} service={s} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 flex flex-col items-center justify-between gap-6 rounded-3xl bg-[var(--kilic-blue)] p-7 text-center text-white sm:p-10 md:flex-row md:text-left"
        >
          <div>
            <h3 className="font-display text-2xl font-extrabold sm:text-3xl">
              Möchten Sie mehr erfahren?
            </h3>
            <p className="mt-1 text-white/70">
              Fordern Sie jetzt Ihr unverbindliches Angebot an.
            </p>
          </div>
          <a
            href={buildWhatsAppLink({
              service: "eine Reinigung",
              message: "Ich hätte gern ein unverbindliches Angebot.",
            })}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-cta-btn"
            className="flex w-full shrink-0 items-center justify-center gap-3 rounded-full bg-[var(--kilic-green)] px-7 py-4 font-bold text-white shadow-lg transition-transform hover:scale-105 md:w-auto"
          >
            <WhatsAppIcon className="h-5 w-5" />
            Jetzt kontaktieren
          </a>
        </motion.div>
      </div>
    </section>
  );
};
